import type { GridApi, IRowNode } from '@ag-grid-community/core';
import { onBeforeUnmount, shallowRef } from 'vue';

import type { SharingPartnerRow } from '../types';

/**
 * Rows hidden by a filter are dropped from the selection, so the count in the
 * bulk action bar always matches what an action would act on.
 */
export default function usePartnerTabSelection() {
  const gridApi = shallowRef<GridApi<SharingPartnerRow> | null>(null);
  const selectedRows = shallowRef<SharingPartnerRow[]>([]);

  function liveApi() {
    const api = gridApi.value;
    return api && !api.isDestroyed() ? api : null;
  }

  /**
   * Deselecting re-fires `selectionChanged`, but that second pass finds nothing
   * stale and so settles immediately.
   */
  function syncSelection() {
    const api = liveApi();
    if (!api) return;

    const visibleIds = new Set<string>();
    api.forEachNodeAfterFilter((node) => {
      if (node.id) visibleIds.add(node.id);
    });

    const kept: SharingPartnerRow[] = [];
    const stale: IRowNode<SharingPartnerRow>[] = [];
    for (const node of api.getSelectedNodes()) {
      if (node.id && visibleIds.has(node.id)) {
        if (node.data) kept.push(node.data);
      } else {
        stale.push(node);
      }
    }

    if (stale.length) api.setNodesSelected({ nodes: stale, newValue: false });
    selectedRows.value = kept;
  }

  function handleGridReady({ api }: { api: GridApi<SharingPartnerRow> }) {
    gridApi.value = api;
    api.addEventListener('selectionChanged', syncSelection);
    api.addEventListener('filterChanged', syncSelection);
  }

  onBeforeUnmount(() => {
    const api = liveApi();
    if (!api) return;
    api.removeEventListener('selectionChanged', syncSelection);
    api.removeEventListener('filterChanged', syncSelection);
  });

  return { selectedRows, handleGridReady };
}
